import { useEffect, useRef, useState } from "react";

import { DEFAULT_LAT, DEFAULT_LNG } from "../../../consts/geo";

import { EmergencySituationType } from "../../../types/emergency";

import { getDefaultPolygon } from "../../../libs/geo";
import { Coordinates } from "../../../types/location";
import { MAP_SIZE_TYPE } from "../NaverMapWithSkeleton";

type Props = {
  shelter?: EmergencySituationType;
  myCoordinate?: Coordinates;
  size?: MAP_SIZE_TYPE;
  handleClickShelterImg?: (e: React.MouseEvent) => void;
};

export default function NaverMap({
  shelter,
  myCoordinate,
  size = "default",
  handleClickShelterImg,
}: Props) {
  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<naver.maps.Map | null>(null);
  const [_, setMarkerList] = useState<naver.maps.Marker[]>([]);
  const polygonRef = useRef<naver.maps.Polygon | null>(null);

  const markerRef = useRef<naver.maps.Marker | null>(null);

  const NAVER_MAP_LOAD_INTERVAL = 1000;

  const loadNaverMap = () => {
    // 네이버 지도에 오버레이되는 UI가 중첩되어 오류처럼 출력되는 문제 때문에, mapRef로 체크
    if (mapDivRef.current && !mapRef.current) {
      let center = undefined;
      if (window?.naver?.maps?.LatLng) {
        center = new window.naver.maps.LatLng(
          Number(shelter?.shelterLat ?? DEFAULT_LAT),
          Number(shelter?.shelterLng ?? DEFAULT_LNG)
        );
      }

      const mapOptions: naver.maps.MapOptions = {
        center,
        zoom: 16,
        scaleControl: false,
        logoControl: false,
      };

      if (window?.naver?.maps?.Map) {
        mapRef.current = new window.naver.maps.Map(
          mapDivRef.current,
          mapOptions
        );

        polygonRef.current = new window.naver.maps.Polygon(
          getDefaultPolygon(mapRef.current)
        );
        return true;
      } else {
        return false;
      }
    } else {
      // console.log(
      //   "Naver Map is already loaded."
      //   mapDivRef.current,
      //   mapRef.current
      // );
      return false;
    }
  };

  useEffect(() => {
    // console.log(
    //   "맵 useEffect : ",
    //   myCoordinate,
    //   mapRef.current,
    //   myCoordinate?.lat,
    //   myCoordinate?.lng
    // );
    if (
      markerRef.current &&
      myCoordinate &&
      myCoordinate.lat &&
      myCoordinate.lng
    ) {
      // 갱신 로직
      // console.log("마커 갱신");
      const newPosition = new window.naver.maps.LatLng(
        myCoordinate?.lat,
        myCoordinate?.lng
      );
      markerRef.current.setPosition(newPosition); // 현재 위치 갱신
    }

    if (
      !markerRef.current &&
      myCoordinate &&
      myCoordinate.lat &&
      myCoordinate.lng &&
      mapRef.current
    ) {
      // 초기화 로직
      // console.log("마커 초기화");
      const marker = new window.naver.maps.Marker({
        position: new window.naver.maps.LatLng(
          myCoordinate.lat,
          myCoordinate.lng
        ),
        map: mapRef.current,
        icon: {
          content: `
            <div style="position: relative; width: 35px; height: 35px;">
              <!-- 바깥 반투명한 원 -->
              <div style="width: 100%; height: 100%; background-color: rgba(0, 123, 255, 0.3); border-radius: 50%; position: absolute; top: 0; left: 0;"></div>
              <!-- 안쪽 파란색 원 -->
              <div style="width: 18px; height: 18px; background-color: #007bff; border: 2px solid white; border-radius: 50%; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);"></div>
            </div>`,
          anchor: new window.naver.maps.Point(12, 12), // 중앙에 맞추기 위한 anchor 설정
        },
      });

      // 마커를 ref에 저장
      markerRef.current = marker;
    }
  }, [myCoordinate, mapRef.current, myCoordinate?.lat, myCoordinate?.lng]);

  useEffect(() => {
    // 마커 및 폴리곤 처리 로직
    if (mapRef.current) {
      if ((shelter?.shelterCoordinateList?.length ?? 0) === 0) {
        polygonRef.current?.setMap(null);
        if (mapRef.current) {
          polygonRef.current = new window.naver.maps.Polygon(
            getDefaultPolygon(mapRef.current)
          );
        }
      } else {
        polygonRef.current?.setPaths([
          shelter?.shelterCoordinateList?.map(
            (coord) =>
              new window.naver.maps.LatLng(Number(coord.lat), Number(coord.lng))
          ) ?? [],
        ]);
      }
    }
  }, [mapRef.current, shelter?.shelterCoordinateList]);

  useEffect(() => {
    const result = loadNaverMap();
    if (result === false) {
      const interval = setInterval(() => {
        loadNaverMap();
      }, NAVER_MAP_LOAD_INTERVAL);

      return () => {
        clearInterval(interval);
      };
    }
  }, [mapDivRef.current]);

  useEffect(() => {
    if (mapRef.current) {
      if (shelter?.shelterCoordinateList) {
        mapRef.current?.setCenter(
          new naver.maps.LatLng(
            shelter.shelterCoordinateList.reduce((acc, cur) => {
              return acc + cur.lat;
            }, 0) / shelter.shelterCoordinateList.length,
            shelter.shelterCoordinateList.reduce((acc, cur) => {
              return acc + cur.lng;
            }, 0) / shelter.shelterCoordinateList.length
          )
        );
      } else {
        // 이론상 없는 케이스
        mapRef.current.setCenter(
          new window.naver.maps.LatLng(
            Number(shelter?.shelterLat ?? DEFAULT_LAT),
            Number(shelter?.shelterLng ?? DEFAULT_LNG)
          )
        );
      }

      const mapLogoContainer = document.querySelector(
        "#map > div:nth-child(3)"
      );

      // 로고 클릭시 버블링 방지
      if (mapLogoContainer) {
        const handleClick = (event: Event) => {
          const mouseEvent = event as MouseEvent;
          mouseEvent.stopPropagation();
        };

        mapLogoContainer.addEventListener("click", handleClick);

        return () => {
          mapLogoContainer.removeEventListener("click", handleClick);
        };
      }
    } else {
      console.log("Map is not ready yet.");
    }
  }, [
    mapRef.current,
    shelter?.lat,
    shelter?.lng,
    shelter?.shelterCoordinateList,
  ]);

  return (
    <div
      ref={mapDivRef}
      id="map"
      className={"w-full h-full relative"}
      data-testid="naver-map"
    >
      {size !== "full" && (
        <div
          className="absolute bottom-5 z-10 rounded-[10px] bg-white left-1/2 -translate-x-1/2 w-[calc(100%-40px)] shadow-md"
          data-testid="naver-map-shelter-overlay"
        >
          <div
            data-testid="naver-map-shelter-overlay-content"
            className="flex gap-4 items-center p-5"
          >
            <img
              src={shelter?.shelterImg?.items[0]?.url}
              alt="shelterImg"
              data-testid="naver-map-shelter-overlay-shelter-img"
              className="w-[20vw] h-[12vw] object-cover rounded-[10px]"
              onClick={handleClickShelterImg ? handleClickShelterImg : () => {}}
            />
            <div
              className="flex flex-col"
              data-testid="naver-map-shelter-overlay-shelter-info"
            >
              <p
                data-testid="naver-map-shelter-overlay-incident-address"
                className="text-base leading-[19px] font-semibold m-0"
              >
                {shelter?.shelterName}
              </p>
              <p
                data-testid="naver-map-shelter-overlay-shelter-name"
                className="text-sm leading-[24px] m-0 text-brand-neutral-60"
              >
                {shelter?.shelterDescription ?? "-"}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
